import React, { useState, useEffect } from 'react';
import { Eye, Database, Search, ArrowRight, Image, Plus, Download, Tag, FlaskConical, Edit3, Layers, Rocket, Globe, Package, List, Youtube, Sparkles, ArrowLeft } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import {
  PROSPECTOR_ENGINE_URL,
  KNOWLEDGE_ENGINE_URL,
  ORCHESTRATION_ENGINE_URL,
  AUTHORING_ENGINE_URL,
  STUDIO_ENGINE_URL,
  CANON_ENGINE_URL,
  ASSEMBLY_ENGINE_URL,
  PUBLISHING_ENGINE_URL,
  CURATION_ENGINE_URL,
} from '../lib/engineUrls';

interface EngineStatus {
  id: string;
  name: string;
  status: 'idle' | 'processing' | 'completed' | 'error';
  progress: number;
  lastActivity: string;
  port: number;
  health: boolean;
  url: string;
}

type ViewMode = 'overview' | 'prospector' | 'knowledge' | 'curation' | 'canon' | 'orchestration' | 'authoring' | 'studio' | 'assembly' | 'publishing';

export default function App() {
  const [engines, setEngines] = useState<EngineStatus[]>([
    { id: 'prospector',    name: 'Prospector Engine',    status: 'idle', progress: 0, lastActivity: 'Ready', port: 4001, health: false, url: PROSPECTOR_ENGINE_URL },
    { id: 'knowledge',     name: 'Knowledge Engine',     status: 'idle', progress: 0, lastActivity: 'Ready', port: 4002, health: false, url: KNOWLEDGE_ENGINE_URL },
    { id: 'curation',      name: 'Curation Engine',      status: 'idle', progress: 0, lastActivity: 'Ready', port: 4101, health: false, url: CURATION_ENGINE_URL },
    { id: 'canon',         name: 'Canon Engine',         status: 'idle', progress: 0, lastActivity: 'Ready', port: 4007, health: false, url: CANON_ENGINE_URL },
    { id: 'orchestration', name: 'Orchestration Engine', status: 'idle', progress: 0, lastActivity: 'Ready', port: 4004, health: false, url: ORCHESTRATION_ENGINE_URL },
    { id: 'authoring',     name: 'Authoring Engine',     status: 'idle', progress: 0, lastActivity: 'Ready', port: 4005, health: false, url: AUTHORING_ENGINE_URL },
    { id: 'studio',        name: 'Studio Engine',        status: 'idle', progress: 0, lastActivity: 'Ready', port: 4006, health: false, url: STUDIO_ENGINE_URL },
    { id: 'assembly',      name: 'Assembly Engine',      status: 'idle', progress: 0, lastActivity: 'Ready', port: 4009, health: false, url: ASSEMBLY_ENGINE_URL },
    { id: 'publishing',    name: 'Publishing Engine',    status: 'idle', progress: 0, lastActivity: 'Ready', port: 4010, health: false, url: PUBLISHING_ENGINE_URL },
  ]);

  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [notionConfig, setNotionConfig] = useState<{ apiKey: string; databaseId: string }>(() => {
    const saved = localStorage.getItem('notionConfig');
    return saved ? JSON.parse(saved) : { apiKey: '', databaseId: '' };
  });
  const [createSeparatePages, setCreateSeparatePages] = useState(true);

  // OCR-specific state
  const [ocrImageUrl, setOcrImageUrl] = useState('');
  const [ocrResult, setOcrResult] = useState<any>(null);
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Discovery-specific state
  const [discoverySource, setDiscoverySource] = useState('');
  const [discoveryResults, setDiscoveryResults] = useState<any>(null);

  // Capture-specific state
  const [captureUrl, setCaptureUrl] = useState('');
  const [captureResults, setCaptureResults] = useState<any>(null);

  // YouTube-specific state
  const [youtubeQuery, setYoutubeQuery] = useState('');
  const [youtubeResults, setYoutubeResults] = useState<any[]>([]);

  // Save notionConfig to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('notionConfig', JSON.stringify(notionConfig));
  }, [notionConfig]);

  // Check engine health on load
  useEffect(() => {
    checkEngineHealth();
  }, []);

  // Optional: Periodic health checks (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      checkEngineHealth();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const checkEngineHealth = async () => {
    console.log('🔍 Checking engine health...');
    const updatedEngines = await Promise.all(
      engines.map(async (engine) => {
        try {
          const response = await fetch(`${engine.url}/health`, {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
            signal: AbortSignal.timeout(5000)
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const data = await response.json();
          return {
            ...engine,
            health: data.status === 'healthy',
            lastActivity: data.status === 'healthy' ? 'Online' : 'Offline'
          };
        } catch (error: any) {
          console.error(`❌ Health check failed for ${engine.name}:`, error.message);
          return {
            ...engine,
            health: false,
            lastActivity: `Error: ${error.message}`
          };
        }
      })
    );
    setEngines(updatedEngines);
  };

  // ========== PROSPECTOR ENGINE FUNCTIONS ==========

  // OCR Functions
  const handleOcrUpload = async (file: File) => {
    setIsOcrProcessing(true);
    setOcrResult(null);
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/ocr/extract`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      if (data.success) {
        setOcrResult(data);
        setEngines(prev => prev.map(e => 
          e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: 'OCR completed', progress: 100 } : e
        ));
      } else {
        throw new Error(data.error || 'OCR failed');
      }
    } catch (error: any) {
      setOcrResult({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    } finally {
      setIsOcrProcessing(false);
    }
  };

  const handleOcrFromUrl = async () => {
    if (!ocrImageUrl) {
      alert('Please enter an image URL');
      return;
    }

    setIsOcrProcessing(true);
    setOcrResult(null);
    
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/ocr/extract-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: ocrImageUrl })
      });
      
      const data = await response.json();
      if (data.success) {
        setOcrResult(data);
        setEngines(prev => prev.map(e => 
          e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: 'OCR completed', progress: 100 } : e
        ));
      } else {
        throw new Error(data.error || 'OCR failed');
      }
    } catch (error: any) {
      setOcrResult({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    } finally {
      setIsOcrProcessing(false);
    }
  };

  // Discovery Functions
  const handleDiscovery = async () => {
    if (!discoverySource) {
      alert('Please enter a source URL');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'prospector' ? { ...e, status: 'processing', lastActivity: 'Discovering content...', progress: 0 } : e
    ));
    
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/discovery/discover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: discoverySource })
      });
      
      const data = await response.json();
      setDiscoveryResults(data);
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: 'Discovery completed', progress: 100 } : e
      ));
    } catch (error: any) {
      setDiscoveryResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  // Capture Functions
  const handleCapture = async () => {
    if (!captureUrl) {
      alert('Please enter a URL to capture');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'prospector' ? { ...e, status: 'processing', lastActivity: 'Capturing content...', progress: 0 } : e
    ));
    
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/capture/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: captureUrl })
      });
      
      const data = await response.json();
      setCaptureResults(data);
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: 'Capture completed', progress: 100 } : e
      ));
    } catch (error: any) {
      setCaptureResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  // YouTube Functions
  const handleYouTubeSearch = async () => {
    if (!youtubeQuery) {
      alert('Please enter a search query');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'prospector' ? { ...e, status: 'processing', lastActivity: 'Searching YouTube...', progress: 0 } : e
    ));
    
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/youtube/search?query=${encodeURIComponent(youtubeQuery)}&maxResults=10`);
      const data = await response.json();
      setYoutubeResults(data.videos || []);
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: `Found ${data.videos?.length || 0} videos`, progress: 100 } : e
      ));
    } catch (error: any) {
      setYoutubeResults([]);
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const handleGetTranscript = async (videoId: string) => {
    setEngines(prev => prev.map(e => 
      e.id === 'prospector' ? { ...e, status: 'processing', lastActivity: 'Fetching transcript...', progress: 0 } : e
    ));
    
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/youtube/transcript/${videoId}`);
      const data = await response.json();
      
      // Show transcript in a modal or alert
      const transcriptText = data.transcript?.map((t: any) => t.text).join(' ') || 'No transcript available';
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`<html><head><title>Transcript - ${videoId}</title></head><body><pre style="white-space: pre-wrap; padding: 20px;">${transcriptText}</pre></body></html>`);
      }
      
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: 'Transcript fetched', progress: 100 } : e
      ));
    } catch (error: any) {
      alert(`Failed to fetch transcript: ${error.message}`);
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  // Staging Functions
  const [stagingContent, setStagingContent] = useState('');
  const [stagingResults, setStagingResults] = useState<any>(null);

  const handleStaging = async () => {
    if (!stagingContent.trim()) {
      alert('Please enter content to stage');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'prospector' ? { ...e, status: 'processing', lastActivity: 'Staging content...', progress: 0 } : e
    ));
    
    try {
      const contentToStage = {
        id: `content_${Date.now()}`,
        body: stagingContent,
        metadata: { source: 'manual', timestamp: new Date().toISOString() }
      };
      
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/staging/stage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contentToStage)
      });
      
      const data = await response.json();
      setStagingResults(data);
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: 'Content staged', progress: 100 } : e
      ));
    } catch (error: any) {
      setStagingResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  // AI Recommendations
  const [aiContent, setAiContent] = useState('');
  const [aiRecommendations, setAiRecommendations] = useState<any>(null);

  const handleGetRecommendations = async () => {
    if (!aiContent.trim()) {
      alert('Please enter content to analyze');
      return;
    }

    setEngines(prev => prev.map(e => 
      e.id === 'prospector' ? { ...e, status: 'processing', lastActivity: 'Getting AI recommendations...', progress: 0 } : e
    ));
    
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/ai-recommendations/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: aiContent })
      });
      
      const data = await response.json();
      setAiRecommendations(data);
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: 'Recommendations ready', progress: 100 } : e
      ));
    } catch (error: any) {
      setAiRecommendations({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  // Transfer to Knowledge Engine
  const [transferContent, setTransferContent] = useState('');
  const [transferResults, setTransferResults] = useState<any>(null);

  const handleTransferToKnowledge = async () => {
    if (!transferContent.trim()) {
      alert('Please enter content to transfer');
      return;
    }

    setEngines(prev => prev.map(e => 
      e.id === 'prospector' ? { ...e, status: 'processing', lastActivity: 'Transferring to Knowledge Engine...', progress: 0 } : e
    ));
    
    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/transfer/to-knowledge-engine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: {
            body: transferContent,
            metadata: { source: 'prospector', timestamp: new Date().toISOString() }
          }
        })
      });

      const data = await response.json();
      setTransferResults(data);
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: 'Transfer completed', progress: 100 } : e
      ));
    } catch (error: any) {
      setTransferResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  // Batch OCR
  const [batchOcrFiles, setBatchOcrFiles] = useState<File[]>([]);
  const [batchOcrResults, setBatchOcrResults] = useState<any[]>([]);

  const handleBatchOcr = async () => {
    if (batchOcrFiles.length === 0) {
      alert('Please select images to process');
      return;
    }

    setIsOcrProcessing(true);
    setBatchOcrResults([]);
    setEngines(prev => prev.map(e => 
      e.id === 'prospector' ? { ...e, status: 'processing', lastActivity: `Processing ${batchOcrFiles.length} images...`, progress: 0 } : e
    ));
    
    try {
      const formData = new FormData();
      batchOcrFiles.forEach(file => {
        formData.append('images', file);
      });
      
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/ocr/batch-extract`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setBatchOcrResults(data.results || []);
        setEngines(prev => prev.map(e => 
          e.id === 'prospector' ? { ...e, status: 'completed', lastActivity: `Processed ${data.results?.length || 0} images`, progress: 100 } : e
        ));
      } else {
        throw new Error(data.error || 'Batch OCR failed');
      }
    } catch (error: any) {
      setBatchOcrResults([{ error: error.message }]);
      setEngines(prev => prev.map(e => 
        e.id === 'prospector' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    } finally {
      setIsOcrProcessing(false);
    }
  };

  const handleBatchOcrFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    setBatchOcrFiles(imageFiles);
  };

  // Notion Functions (kept from original)
  const processFolderToNotion = async () => {
    if (uploadedFiles.length === 0 || !notionConfig.apiKey || !notionConfig.databaseId) {
      alert('Please upload images and configure Notion settings');
      return;
    }

    setIsProcessing(true);

    try {
          setEngines(prev => prev.map(engine => 
        engine.id === 'prospector' ? { 
          ...engine, 
          status: 'processing' as const,
          lastActivity: 'Testing Notion connection...',
          progress: 0
        } : engine
      ));

      const connectionTest = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey: notionConfig.apiKey,
          databaseId: notionConfig.databaseId
        })
      });

      if (!connectionTest.ok) {
        throw new Error('Notion connection test failed');
      }

      setEngines(prev => prev.map(engine => 
        engine.id === 'prospector' ? { 
          ...engine, 
          lastActivity: 'Processing images to Notion...',
          progress: 50
        } : engine
      ));

      const formData = new FormData();
      formData.append('apiKey', notionConfig.apiKey);
      formData.append('databaseId', notionConfig.databaseId);
      formData.append('createSeparatePages', createSeparatePages.toString());
      
      uploadedFiles.forEach(file => {
        formData.append('images', file);
      });

      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion/batch-process`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Batch processing failed');
      }

      const result = await response.json();
      setResults(result.results || []);

      setEngines(prev => prev.map(engine => ({
        ...engine,
        status: 'completed' as const,
        progress: 100,
        lastActivity: 'Completed'
      })));

    } catch (error: any) {
      console.error('Folder processing failed:', error);
      setEngines(prev => prev.map(engine => ({
        ...engine,
        status: 'error' as const,
        lastActivity: 'Error occurred'
      })));
      alert(`Processing failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const testNotionConnection = async () => {
    if (!notionConfig.apiKey || !notionConfig.databaseId) {
      alert('Please enter your Notion API key and database ID');
      return;
    }

    try {
      const response = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion/test-connection`, {
                method: 'POST',
        headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
          apiKey: notionConfig.apiKey,
          databaseId: notionConfig.databaseId
            })
          });

      const result = await response.json();
      
      if (result.success) {
        alert('✅ Notion connection successful!');
          } else {
        alert(`❌ Notion connection failed: ${result.error}`);
      }
    } catch (error: any) {
      alert(`❌ Connection test failed: ${error.message}`);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleOcrUpload(file);
    }
  };

  const handleFolderUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    setUploadedFiles(imageFiles);
  };

  // ========== RENDER FUNCTIONS ==========

  const renderOverview = () => (
    <div className="space-y-8">
                        <div>
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-4">Engine Overview</h2>
        <p className="text-[var(--text-sec)] mb-6">Select an engine to access its tools and capabilities.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {engines.map(engine => (
                        <button 
              key={engine.id}
              onClick={() => setViewMode(engine.id as ViewMode)}
              className="p-6 bg-[var(--bg-raised)] shadow-sm border-2 hover:border-[var(--slate)] transition-all text-left"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[var(--text-pri)]">{engine.name}</h3>
                <div className={`w-3 h-3 rounded-full ${engine.health ? 'bg-[var(--green)]' : 'bg-[var(--coral)]'}`}></div>
                          </div>
              <p className="text-sm text-[var(--text-sec)] mb-2">{engine.lastActivity}</p>
              <p className="text-xs text-[var(--text-ter)]">Port: {engine.port}</p>
                        </button>
          ))}
        </div>
      </div>

      {/* Pipeline Workflow - Separate Section */}
                        <div>
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-4">Content Pipeline Workflow</h2>
        <p className="text-[var(--text-sec)] mb-6">Follow the complete journey from content discovery to publication.</p>
        
                    <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
          <div className="space-y-4">
            {[
              { step: 1, name: 'Gathering', engine: 'Prospector', icon: Search, color: 'blue' },
              { step: 2, name: 'Tagging', engine: 'Knowledge', icon: Tag, color: 'green' },
              { step: 3, name: 'Formulating', engine: 'Curation', icon: FlaskConical, color: 'purple' },
              { step: 4, name: 'Creating', engine: 'Authoring/Studio', icon: Edit3, color: 'orange' },
              { step: 5, name: 'Applying', engine: 'Assembly', icon: Layers, color: 'indigo' },
              { step: 6, name: 'Publishing', engine: 'Publishing', icon: Rocket, color: 'red' }
            ].map((stage) => (
              <div key={stage.step} className="flex items-center gap-4 p-4 border">
                <div className={`w-12 h-12 bg-${stage.color}-100  flex items-center justify-center`}>
                  <stage.icon className={`h-6 w-6 text-${stage.color}-600`} />
                        </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[var(--text-pri)]">Step {stage.step}: {stage.name}</h3>
                  <p className="text-sm text-[var(--text-sec)]">Engine: {stage.engine}</p>
                        </div>
                <RouterLink
                  to="/workflow"
                  className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] text-sm"
                >
                  View Workflow
                </RouterLink>
                          </div>
            ))}
                          </div>
                    </div>
                        </div>
                        </div>
  );
                      
  const renderProspectorEngine = () => (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
                        <button 
            onClick={() => setViewMode('overview')}
            className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
                        </button>
          <h2 className="text-3xl font-bold text-[var(--text-pri)]">🔍 Prospector Engine</h2>
          <p className="text-[var(--text-sec)] mt-2">Content discovery, capture, OCR, and staging</p>
                          </div>
        <div className={`px-4 py-2  ${engines.find(e => e.id === 'prospector')?.health ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
          {engines.find(e => e.id === 'prospector')?.health ? 'Online' : 'Offline'}
                      </div>
                    </div>

      {/* OCR Section */}
                    <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Image className="w-5 h-5 text-[var(--text-ter)]" />
          OCR - Text Extraction
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Extract text from images using optical character recognition.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Upload Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="w-full border border-[var(--rule)] px-3 py-2"
              disabled={isOcrProcessing}
            />
            {isOcrProcessing && (
              <p className="text-sm text-[var(--text-ter)] mt-2">Processing image...</p>
            )}
                      </div>
                      
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Or Enter Image URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={ocrImageUrl}
                onChange={(e) => setOcrImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 border border-[var(--rule)] px-3 py-2"
                disabled={isOcrProcessing}
              />
                        <button 
                onClick={handleOcrFromUrl}
                disabled={!ocrImageUrl || isOcrProcessing}
                className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
              >
                Extract
                        </button>
                          </div>
                      </div>
                    </div>

        {ocrResult && (
          <div className="mt-6 p-4 bg-[var(--surface)]">
            {ocrResult.error ? (
              <p className="text-[var(--coral)]">Error: {ocrResult.error}</p>
            ) : (
                        <div>
                <h4 className="font-semibold mb-2">Extracted Text:</h4>
                <pre className="whitespace-pre-wrap bg-[var(--bg-raised)] p-4 rounded border text-sm">{ocrResult.result?.text || 'No text found'}</pre>
                {ocrResult.result?.confidence && (
                  <p className="text-sm text-[var(--text-sec)] mt-2">Confidence: {Math.round(ocrResult.result.confidence * 100)}%</p>
                )}
                        <button 
                  onClick={() => navigator.clipboard.writeText(ocrResult.result?.text || '')}
                  className="mt-2 px-3 py-1 bg-[var(--slate)] text-white rounded text-sm hover:bg-[var(--slate)]"
                >
                  Copy Text
                        </button>
                      </div>
            )}
                    </div>
        )}

        {/* Batch OCR */}
        <div className="mt-6 pt-6 border-t">
          <h4 className="font-semibold text-[var(--text-pri)] mb-3">Batch OCR Processing</h4>
          <div className="space-y-3">
                        <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Select Multiple Images</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleBatchOcrFileSelect}
                className="w-full border border-[var(--rule)] px-3 py-2"
                disabled={isOcrProcessing}
              />
              {batchOcrFiles.length > 0 && (
                <p className="text-sm text-[var(--text-sec)] mt-2">{batchOcrFiles.length} images selected</p>
              )}
                        </div>
                        <button 
              onClick={handleBatchOcr}
              disabled={batchOcrFiles.length === 0 || isOcrProcessing}
              className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
            >
              {isOcrProcessing ? 'Processing...' : `Process ${batchOcrFiles.length} Images`}
                        </button>
            {batchOcrResults.length > 0 && (
              <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                {batchOcrResults.map((result: any, index: number) => (
                  <div key={index} className="p-3 bg-[var(--surface)]">
                    <p className="text-sm font-medium">{result.fileName || `Image ${index + 1}`}</p>
                    {result.error ? (
                      <p className="text-xs text-[var(--coral)] mt-1">{result.error}</p>
                    ) : (
                      <p className="text-xs text-[var(--text-sec)] mt-1">
                        {result.text?.substring(0, 100)}...
                      </p>
                    )}
                          </div>
                ))}
                      </div>
            )}
                    </div>
                        </div>
                      </div>
                      
      {/* Discovery Section */}
                    <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-[var(--green)]" />
          Content Discovery
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Discover content from RSS feeds and other sources.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Source URL (RSS feed, API, etc.)</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={discoverySource}
                onChange={(e) => setDiscoverySource(e.target.value)}
                placeholder="https://example.com/rss-feed"
                className="flex-1 border border-[var(--rule)] px-3 py-2"
              />
                        <button 
                onClick={handleDiscovery}
                disabled={!discoverySource}
                className="px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50"
              >
                Discover
                        </button>
                      </div>
                    </div>

          {discoveryResults && (
            <div className="p-4 bg-[var(--surface)]">
              {discoveryResults.error ? (
                <p className="text-[var(--coral)]">Error: {discoveryResults.error}</p>
              ) : (
                        <div>
                  <h4 className="font-semibold mb-2">Discovery Results:</h4>
                  <pre className="whitespace-pre-wrap bg-[var(--bg-raised)] p-4 rounded border text-sm overflow-auto max-h-96">
                    {JSON.stringify(discoveryResults, null, 2)}
                  </pre>
                        </div>
              )}
                      </div>
          )}
                          </div>
              </div>
              
      {/* Capture Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-[var(--text-ter)]" />
          Content Capture
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Capture and extract content from web pages and URLs.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">URL to Capture</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={captureUrl}
                onChange={(e) => setCaptureUrl(e.target.value)}
                placeholder="https://example.com/article"
                className="flex-1 border border-[var(--rule)] px-3 py-2"
              />
                        <button 
                onClick={handleCapture}
                disabled={!captureUrl}
                className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
              >
                Capture
                        </button>
                      </div>
                    </div>

          {captureResults && (
            <div className="p-4 bg-[var(--surface)]">
              {captureResults.error ? (
                <p className="text-[var(--coral)]">Error: {captureResults.error}</p>
              ) : (
                        <div>
                  <h4 className="font-semibold mb-2">Captured Content:</h4>
                  <pre className="whitespace-pre-wrap bg-[var(--bg-raised)] p-4 rounded border text-sm overflow-auto max-h-96">
                    {JSON.stringify(captureResults, null, 2)}
                  </pre>
                        </div>
              )}
                      </div>
          )}
                          </div>
                      </div>

      {/* YouTube Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Youtube className="w-5 h-5 text-[var(--coral)]" />
          YouTube Integration
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Search YouTube videos and extract transcripts.</p>
        
        <div className="space-y-4">
                      <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Search Query</label>
            <div className="flex gap-2">
                        <input
                          type="text"
                value={youtubeQuery}
                onChange={(e) => setYoutubeQuery(e.target.value)}
                placeholder="Search for videos..."
                className="flex-1 border border-[var(--rule)] px-3 py-2"
              />
                        <button 
                onClick={handleYouTubeSearch}
                disabled={!youtubeQuery}
                className="px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)] disabled:opacity-50"
              >
                Search
                        </button>
                      </div>
                    </div>

          {youtubeResults.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold">Search Results:</h4>
              {youtubeResults.map((video: any, index: number) => {
                const videoId = video.id?.videoId || video.id;
                return (
                  <div key={index} className="p-3 bg-[var(--surface)]">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h5 className="font-medium">{video.title || video.snippet?.title}</h5>
                        <p className="text-sm text-[var(--text-sec)] mt-1">{video.snippet?.description?.substring(0, 100)}...</p>
                        {videoId && (
                          <p className="text-xs text-[var(--text-ter)] mt-1">Video ID: {videoId}</p>
                        )}
                      </div>
                      {videoId && (
                        <button 
                          onClick={() => handleGetTranscript(videoId)}
                          className="ml-3 px-3 py-1 bg-[var(--coral)] text-white rounded text-sm hover:bg-[var(--coral)]"
                        >
                          Get Transcript
                        </button>
                      )}
                      </div>
                    </div>
                );
              })}
                          </div>
                        )}
                  </div>
        </div>

      {/* Content Staging Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-[var(--mustard)]" />
          Content Staging
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Validate and prepare content for storage in the Knowledge Engine.</p>
        
            <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Content to Stage</label>
            <textarea
              value={stagingContent}
              onChange={(e) => setStagingContent(e.target.value)}
              placeholder="Paste content here to validate and stage..."
              rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2"
            />
                  </div>
          <button
            onClick={handleStaging}
            disabled={!stagingContent.trim()}
            className="px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] disabled:opacity-50"
          >
            Stage Content
          </button>

          {stagingResults && (
            <div className="p-4 bg-[var(--surface)]">
              {stagingResults.error ? (
                <p className="text-[var(--coral)]">Error: {stagingResults.error}</p>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Staging Result:</h4>
                  <pre className="whitespace-pre-wrap bg-[var(--bg-raised)] p-4 rounded border text-sm overflow-auto max-h-96">
                    {JSON.stringify(stagingResults, null, 2)}
                  </pre>
                  </div>
              )}
                </div>
          )}
                </div>
              </div>
              
      {/* AI Recommendations Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[var(--mustard)]" />
          AI Recommendations
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Get AI-powered recommendations for where to store or organize your content.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Content to Analyze</label>
            <textarea
              value={aiContent}
              onChange={(e) => setAiContent(e.target.value)}
              placeholder="Paste content here to get AI recommendations..."
              rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2"
            />
                  </div>
                <button
            onClick={handleGetRecommendations}
            disabled={!aiContent.trim()}
            className="px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] disabled:opacity-50"
          >
            Get Recommendations
                </button>

          {aiRecommendations && (
            <div className="p-4 bg-[var(--surface)]">
              {aiRecommendations.error ? (
                <p className="text-[var(--coral)]">Error: {aiRecommendations.error}</p>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Recommendations:</h4>
                  {aiRecommendations.recommendations && aiRecommendations.recommendations.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1">
                      {aiRecommendations.recommendations.map((rec: any, index: number) => (
                        <li key={index} className="text-sm">{rec}</li>
                      ))}
                    </ul>
                  ) : (
                    <pre className="whitespace-pre-wrap bg-[var(--bg-raised)] p-4 rounded border text-sm overflow-auto max-h-96">
                      {JSON.stringify(aiRecommendations, null, 2)}
                    </pre>
                  )}
                    </div>
              )}
                  </div>
          )}
        </div>
                      </div>

      {/* Transfer to Knowledge Engine Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <ArrowRight className="w-5 h-5 text-[var(--green)]" />
          Transfer to Knowledge Engine
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Send processed content to the Knowledge Engine for storage and organization.</p>
        
        <div className="space-y-4">
                      <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Content to Transfer</label>
            <textarea
              value={transferContent}
              onChange={(e) => setTransferContent(e.target.value)}
              placeholder="Paste content here to transfer to Knowledge Engine..."
              rows={6}
                          className="w-full border border-[var(--rule)] px-3 py-2"
                        />
                      </div>
          <button
            onClick={handleTransferToKnowledge}
            disabled={!transferContent.trim()}
            className="px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50"
          >
            Transfer to Knowledge Engine
          </button>

          {transferResults && (
            <div className="p-4 bg-[var(--surface)]">
              {transferResults.error ? (
                <p className="text-[var(--coral)]">Error: {transferResults.error}</p>
              ) : (
                      <div>
                  <h4 className="font-semibold mb-2">Transfer Result:</h4>
                  <pre className="whitespace-pre-wrap bg-[var(--bg-raised)] p-4 rounded border text-sm overflow-auto max-h-96">
                    {JSON.stringify(transferResults, null, 2)}
                  </pre>
                </div>
              )}
                          </div>
                        )}
                      </div>
                    </div>

      {/* Notion Integration Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-[var(--text-ter)]" />
          Notion Integration
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Process images and create Notion pages with extracted text.</p>
        
        <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Notion API Key</label>
                          <input
                            type="password"
                            value={notionConfig.apiKey}
                            onChange={(e) => setNotionConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                            placeholder="notion_..."
                            className="w-full border border-[var(--rule)] px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Database ID</label>
                          <input
                            type="text"
                            value={notionConfig.databaseId}
                            onChange={(e) => setNotionConfig(prev => ({ ...prev, databaseId: e.target.value }))}
                            placeholder="32-character database ID"
                            className="w-full border border-[var(--rule)] px-3 py-2"
                          />
                        </div>
                      </div>
                      
          <div className="flex gap-2">
            <button
              onClick={testNotionConnection}
              className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
            >
              Test Connection
            </button>
            <label className="flex items-center gap-2 px-4 py-2 border cursor-pointer">
                            <input
                type="checkbox"
                              checked={createSeparatePages}
                onChange={(e) => setCreateSeparatePages(e.target.checked)}
                className="w-4 h-4"
                            />
              <span className="text-sm">Create separate pages for each image</span>
                          </label>
                      </div>

                      <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Upload Images</label>
                            <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFolderUpload}
                          className="w-full border border-[var(--rule)] px-3 py-2"
                        />
                        {uploadedFiles.length > 0 && (
              <p className="text-sm text-[var(--text-sec)] mt-2">{uploadedFiles.length} images selected</p>
                        )}
                        </div>

                      <button
                        onClick={processFolderToNotion}
            disabled={uploadedFiles.length === 0 || !notionConfig.apiKey || !notionConfig.databaseId || isProcessing}
            className="w-full px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
            {isProcessing ? 'Processing...' : 'Process Images to Notion'}
                      </button>

          {results.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="font-semibold">Processing Results:</h4>
              {results.map((result: any, index: number) => (
                <div key={index} className="p-3 bg-[var(--surface)]">
                  <p className="text-sm">
                    {result.success ? '✅' : '❌'} {result.fileName || `Item ${index + 1}`}
                  </p>
                  {result.error && <p className="text-xs text-[var(--coral)] mt-1">{result.error}</p>}
                      </div>
              ))}
                      </div>
                    )}
                          </div>
                      </div>
                  </div>
  );

  // ========== CURATION ENGINE FUNCTIONS ==========
  const [researchContent, setResearchContent] = useState('');
  const [researchResults, setResearchResults] = useState<any>(null);
  const [refinementContent, setRefinementContent] = useState('');
  const [refinementOptions, setRefinementOptions] = useState({ improveTitle: true, enhanceDescription: true, improveStructure: true });
  const [refinementResults, setRefinementResults] = useState<any>(null);
  const [frameworkIdea, setFrameworkIdea] = useState('');
  const [frameworkResults, setFrameworkResults] = useState<any>(null);

  const handleResearchAnalyze = async () => {
    if (!researchContent.trim()) {
      alert('Please enter content to analyze');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'curation' ? { ...e, status: 'processing', lastActivity: 'Analyzing content...', progress: 0 } : e
    ));
    
    try {
      const contentToAnalyze = typeof researchContent === 'string' ? JSON.parse(researchContent) : researchContent;
      const response = await fetch(`${CURATION_ENGINE_URL}/api/research/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: contentToAnalyze })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setResearchResults(data.data);
        setEngines(prev => prev.map(e => 
          e.id === 'curation' ? { ...e, status: 'completed', lastActivity: 'Analysis complete', progress: 100 } : e
        ));
      } else {
        throw new Error(data.error?.message || 'Analysis failed');
      }
    } catch (error: any) {
      setResearchResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'curation' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const handleRefineContent = async () => {
    if (!refinementContent.trim()) {
      alert('Please enter content to refine');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'curation' ? { ...e, status: 'processing', lastActivity: 'Refining content...', progress: 0 } : e
    ));
    
    try {
      const contentToRefine = typeof refinementContent === 'string' ? JSON.parse(refinementContent) : refinementContent;
      const response = await fetch(`${CURATION_ENGINE_URL}/api/refinement/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: contentToRefine, options: refinementOptions })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setRefinementResults(data.data);
        setEngines(prev => prev.map(e => 
          e.id === 'curation' ? { ...e, status: 'completed', lastActivity: 'Refinement complete', progress: 100 } : e
        ));
      } else {
        throw new Error(data.error?.message || 'Refinement failed');
      }
    } catch (error: any) {
      setRefinementResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'curation' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const handleDevelopFramework = async () => {
    if (!frameworkIdea.trim()) {
      alert('Please enter an idea to develop into a framework');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'curation' ? { ...e, status: 'processing', lastActivity: 'Developing framework...', progress: 0 } : e
    ));
    
    try {
      const idea = typeof frameworkIdea === 'string' ? JSON.parse(frameworkIdea) : frameworkIdea;
      const response = await fetch(`${CURATION_ENGINE_URL}/api/framework/develop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idea })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setFrameworkResults(data.data);
        setEngines(prev => prev.map(e => 
          e.id === 'curation' ? { ...e, status: 'completed', lastActivity: 'Framework developed', progress: 100 } : e
        ));
      } else {
        throw new Error(data.error?.message || 'Framework development failed');
      }
    } catch (error: any) {
      setFrameworkResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'curation' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const renderCurationEngine = () => (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
                        <button
            onClick={() => setViewMode('overview')}
            className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
                        >
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
                        </button>
          <h2 className="text-3xl font-bold text-[var(--text-pri)]">📚 Curation Engine</h2>
          <p className="text-[var(--text-sec)] mt-2">Research, refinement, and framework development</p>
        </div>
        <div className={`px-4 py-2  ${engines.find(e => e.id === 'curation')?.health ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
          {engines.find(e => e.id === 'curation')?.health ? 'Online' : 'Offline'}
        </div>
                      </div>

      {/* Research Analysis Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-[var(--text-ter)]" />
          Research Analysis
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Analyze content for research opportunities, gaps, and related content.</p>
        
        <div className="space-y-4">
                      <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Content to Analyze (JSON)</label>
            <textarea
              value={researchContent}
              onChange={(e) => setResearchContent(e.target.value)}
              placeholder='{"title": "Content Title", "description": "...", "body": "..."}'
              rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2 font-mono text-sm"
            />
          </div>
          <button
            onClick={handleResearchAnalyze}
            disabled={!researchContent.trim()}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
          >
            Analyze Content
          </button>

          {researchResults && (
            <div className="p-4 bg-[var(--surface)]">
              {researchResults.error ? (
                <p className="text-[var(--coral)]">Error: {researchResults.error}</p>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Analysis Results:</h4>
                  <div className="space-y-3">
                    {researchResults.structure && (
                      <div>
                        <p className="text-sm font-medium text-[var(--text-sec)]">Structure:</p>
                        <pre className="text-xs bg-[var(--bg-raised)] p-2 rounded border overflow-auto">
                          {JSON.stringify(researchResults.structure, null, 2)}
                        </pre>
                          </div>
                        )}
                    {researchResults.gaps && researchResults.gaps.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-[var(--text-sec)]">Gaps Found:</p>
                        <ul className="list-disc list-inside text-sm text-[var(--text-sec)]">
                          {researchResults.gaps.map((gap: any, i: number) => (
                            <li key={i}>{gap.field}: {gap.type} (Priority: {gap.priority})</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {researchResults.recommendations && researchResults.recommendations.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-[var(--text-sec)]">Recommendations:</p>
                        <ul className="list-disc list-inside text-sm text-[var(--text-sec)]">
                          {researchResults.recommendations.map((rec: any, i: number) => (
                            <li key={i}>{rec.suggestion} (Priority: {rec.priority})</li>
                          ))}
                        </ul>
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content Refinement Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Edit3 className="w-5 h-5 text-[var(--text-ter)]" />
          Content Refinement
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Refine and improve content quality, structure, and completeness.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Content to Refine (JSON)</label>
            <textarea
              value={refinementContent}
              onChange={(e) => setRefinementContent(e.target.value)}
              placeholder='{"id": "...", "title": "...", "description": "...", "body": "..."}'
              rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2 font-mono text-sm"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-sec)]">Refinement Options:</label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={refinementOptions.improveTitle}
                onChange={(e) => setRefinementOptions(prev => ({ ...prev, improveTitle: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className="text-sm">Improve Title</span>
                    </label>
            <label className="flex items-center gap-2">
                      <input
                type="checkbox"
                checked={refinementOptions.enhanceDescription}
                onChange={(e) => setRefinementOptions(prev => ({ ...prev, enhanceDescription: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className="text-sm">Enhance Description</span>
            </label>
            <label className="flex items-center gap-2">
                        <input
                type="checkbox"
                checked={refinementOptions.improveStructure}
                onChange={(e) => setRefinementOptions(prev => ({ ...prev, improveStructure: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className="text-sm">Improve Structure</span>
            </label>
                      </div>
          <button
            onClick={handleRefineContent}
            disabled={!refinementContent.trim()}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
          >
            Refine Content
          </button>

          {refinementResults && (
            <div className="p-4 bg-[var(--surface)]">
              {refinementResults.error ? (
                <p className="text-[var(--coral)]">Error: {refinementResults.error}</p>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Refined Content:</h4>
                  <pre className="text-xs bg-[var(--bg-raised)] p-3 rounded border overflow-auto max-h-96">
                    {JSON.stringify(refinementResults, null, 2)}
                  </pre>
                  {refinementResults.suggestions && refinementResults.suggestions.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-[var(--text-sec)]">Suggestions:</p>
                      <ul className="list-disc list-inside text-sm text-[var(--text-sec)] mt-1">
                        {refinementResults.suggestions.map((sug: any, i: number) => (
                          <li key={i}>{sug.message} (Priority: {sug.priority})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Framework Development Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-[var(--text-ter)]" />
          Framework Development
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Develop ideas into coherent, structured frameworks.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Idea to Develop (JSON)</label>
                      <textarea
              value={frameworkIdea}
              onChange={(e) => setFrameworkIdea(e.target.value)}
              placeholder='{"title": "Idea Title", "description": "...", "concepts": ["concept1", "concept2"], "steps": [{"name": "Step 1"}]}'
                        rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2 font-mono text-sm"
            />
          </div>
          <button
            onClick={handleDevelopFramework}
            disabled={!frameworkIdea.trim()}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
          >
            Develop Framework
          </button>

          {frameworkResults && (
            <div className="p-4 bg-[var(--surface)]">
              {frameworkResults.error ? (
                <p className="text-[var(--coral)]">Error: {frameworkResults.error}</p>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Framework:</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-[var(--text-sec)]">Title: {frameworkResults.title}</p>
                      <p className="text-sm text-[var(--text-sec)]">{frameworkResults.description}</p>
                    </div>
                    {frameworkResults.components && frameworkResults.components.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-[var(--text-sec)]">Components ({frameworkResults.components.length}):</p>
                        <ul className="list-disc list-inside text-sm text-[var(--text-sec)] mt-1">
                          {frameworkResults.components.map((comp: any, i: number) => (
                            <li key={i}>{comp.name} ({comp.type})</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {frameworkResults.validation && (
                      <div>
                        <p className="text-sm font-medium text-[var(--text-sec)]">
                          Validation: {frameworkResults.validation.isValid ? '✅ Valid' : '❌ Invalid'}
                        </p>
                        {frameworkResults.validation.issues && frameworkResults.validation.issues.length > 0 && (
                          <ul className="list-disc list-inside text-sm text-[var(--coral)] mt-1">
                            {frameworkResults.validation.issues.map((issue: any, i: number) => (
                              <li key={i}>{issue.message}</li>
                            ))}
                          </ul>
                    )}
                  </div>
                    )}
                    <details className="mt-2">
                      <summary className="text-sm font-medium text-[var(--text-sec)] cursor-pointer">Full Framework JSON</summary>
                      <pre className="text-xs bg-[var(--bg-raised)] p-3 rounded border overflow-auto max-h-64 mt-2">
                        {JSON.stringify(frameworkResults, null, 2)}
                      </pre>
                    </details>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ========== AUTHORING ENGINE FUNCTIONS ==========
  const [contentTitle, setContentTitle] = useState('');
  const [contentDescription, setContentDescription] = useState('');
  const [contentBody, setContentBody] = useState('');
  const [contentTags, setContentTags] = useState('');
  const [createdContent, setCreatedContent] = useState<any>(null);
  const [contentList, setContentList] = useState<any[]>([]);
  const [selectedContentId, setSelectedContentId] = useState('');
  const [contentToUpdate, setContentToUpdate] = useState<any>(null);

  const handleCreateContent = async () => {
    if (!contentTitle.trim()) {
      alert('Please enter a title');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'authoring' ? { ...e, status: 'processing', lastActivity: 'Creating content...', progress: 0 } : e
    ));
    
    try {
      const tagsArray = contentTags.split(',').map(t => t.trim()).filter(t => t);
      const response = await fetch(`${AUTHORING_ENGINE_URL}/api/content/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: contentTitle,
          description: contentDescription,
          body: contentBody,
          tags: tagsArray,
          metadata: {}
        })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setCreatedContent(data.data);
        setEngines(prev => prev.map(e => 
          e.id === 'authoring' ? { ...e, status: 'completed', lastActivity: 'Content created', progress: 100 } : e
        ));
        // Clear form
        setContentTitle('');
        setContentDescription('');
        setContentBody('');
        setContentTags('');
        // Refresh list
        handleListContent();
      } else {
        throw new Error(data.error?.message || 'Content creation failed');
      }
    } catch (error: any) {
      setCreatedContent({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'authoring' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const handleListContent = async () => {
    setEngines(prev => prev.map(e => 
      e.id === 'authoring' ? { ...e, status: 'processing', lastActivity: 'Loading content...', progress: 0 } : e
    ));
    
    try {
      const response = await fetch(`${AUTHORING_ENGINE_URL}/api/content`);
      const data = await response.json();
      if (data.status === 'success') {
        setContentList(data.data || []);
        setEngines(prev => prev.map(e => 
          e.id === 'authoring' ? { ...e, status: 'completed', lastActivity: `Loaded ${data.data?.length || 0} items`, progress: 100 } : e
        ));
      } else {
        throw new Error(data.error?.message || 'Failed to load content');
      }
    } catch (error: any) {
      setContentList([]);
      setEngines(prev => prev.map(e => 
        e.id === 'authoring' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const handleGetContent = async () => {
    if (!selectedContentId.trim()) {
      alert('Please enter a content ID');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'authoring' ? { ...e, status: 'processing', lastActivity: 'Loading content...', progress: 0 } : e
    ));
    
    try {
      const response = await fetch(`${AUTHORING_ENGINE_URL}/api/content/${selectedContentId}`);
      const data = await response.json();
      if (data.status === 'success') {
        setContentToUpdate(data.data);
        setEngines(prev => prev.map(e => 
          e.id === 'authoring' ? { ...e, status: 'completed', lastActivity: 'Content loaded', progress: 100 } : e
        ));
      } else {
        throw new Error(data.error?.message || 'Content not found');
      }
    } catch (error: any) {
      setContentToUpdate({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'authoring' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const renderAuthoringEngine = () => (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
                      <button
            onClick={() => setViewMode('overview')}
            className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
                      >
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
                      </button>
          <h2 className="text-3xl font-bold text-[var(--text-pri)]">✍️ Authoring Engine</h2>
          <p className="text-[var(--text-sec)] mt-2">Create, edit, and manage content</p>
        </div>
        <div className={`px-4 py-2  ${engines.find(e => e.id === 'authoring')?.health ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
          {engines.find(e => e.id === 'authoring')?.health ? 'Online' : 'Offline'}
        </div>
      </div>

      {/* Create Content Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[var(--green)]" />
          Create Content
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Create new content with title, description, body, and tags.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Title *</label>
            <input
              type="text"
              value={contentTitle}
              onChange={(e) => setContentTitle(e.target.value)}
              placeholder="Enter content title"
              className="w-full border border-[var(--rule)] px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Description</label>
            <textarea
              value={contentDescription}
              onChange={(e) => setContentDescription(e.target.value)}
              placeholder="Enter content description"
              rows={3}
              className="w-full border border-[var(--rule)] px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Body</label>
            <textarea
              value={contentBody}
              onChange={(e) => setContentBody(e.target.value)}
              placeholder="Enter content body"
              rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Tags (comma-separated)</label>
            <input
              type="text"
              value={contentTags}
              onChange={(e) => setContentTags(e.target.value)}
              placeholder="tag1, tag2, tag3"
              className="w-full border border-[var(--rule)] px-3 py-2"
            />
          </div>
                      <button
            onClick={handleCreateContent}
            disabled={!contentTitle.trim()}
            className="px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50"
          >
            Create Content
                      </button>

          {createdContent && (
            <div className="p-4 bg-[var(--surface)]">
              {createdContent.error ? (
                <p className="text-[var(--coral)]">Error: {createdContent.error}</p>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Content Created:</h4>
                  <p className="text-sm text-[var(--text-sec)]">ID: {createdContent.id}</p>
                  <p className="text-sm text-[var(--text-sec)]">Title: {createdContent.title}</p>
                  <p className="text-sm text-[var(--text-sec)]">Status: {createdContent.status}</p>
                      <button
                    onClick={() => navigator.clipboard.writeText(createdContent.id)}
                    className="mt-2 px-3 py-1 bg-[var(--slate)] text-white rounded text-xs hover:bg-[var(--slate)]"
                      >
                    Copy Content ID
                      </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* List Content Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <List className="w-5 h-5 text-[var(--text-ter)]" />
          Content Library
        </h3>
        <p className="text-[var(--text-sec)] mb-4">View and manage all created content.</p>
        
        <div className="space-y-4">
                    <button
            onClick={handleListContent}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
                    >
            Load Content List
                    </button>
                    
          {contentList.length > 0 && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {contentList.map((item: any) => (
                <div key={item.id} className="p-3 bg-[var(--surface)]">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-[var(--text-pri)]">{item.title}</p>
                      <p className="text-sm text-[var(--text-sec)] mt-1">{item.description || 'No description'}</p>
                      <p className="text-xs text-[var(--text-ter)] mt-1">ID: {item.id}</p>
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {item.tags.map((tag: string, i: number) => (
                            <span key={i} className="px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded text-xs">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedContentId(item.id);
                        handleGetContent();
                      }}
                      className="ml-3 px-3 py-1 bg-[var(--slate)] text-white rounded text-xs hover:bg-[var(--bg-raised)]"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
                  </div>

      {/* Get/Update Content Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-[var(--text-ter)]" />
          View Content
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Get content by ID to view or update.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Content ID</label>
            <div className="flex gap-2">
                        <input
                          type="text"
                value={selectedContentId}
                onChange={(e) => setSelectedContentId(e.target.value)}
                placeholder="Enter content ID"
                          className="flex-1 border border-[var(--rule)] px-3 py-2"
                        />
                        <button
                onClick={handleGetContent}
                disabled={!selectedContentId.trim()}
                className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
              >
                Load
                        </button>
                      </div>
                    </div>

          {contentToUpdate && (
            <div className="p-4 bg-[var(--surface)]">
              {contentToUpdate.error ? (
                <p className="text-[var(--coral)]">Error: {contentToUpdate.error}</p>
              ) : (
                      <div>
                  <h4 className="font-semibold mb-2">Content Details:</h4>
                  <pre className="text-xs bg-[var(--bg-raised)] p-3 rounded border overflow-auto max-h-96">
                    {JSON.stringify(contentToUpdate, null, 2)}
                  </pre>
                          </div>
                        )}
                      </div>
                    )}
        </div>
      </div>
    </div>
  );

  // ========== ASSEMBLY ENGINE FUNCTIONS ==========
  const [assemblyContent, setAssemblyContent] = useState('');
  const [assemblyAssets, setAssemblyAssets] = useState('');
  const [assemblyTemplate, setAssemblyTemplate] = useState('');
  const [assemblyResults, setAssemblyResults] = useState<any>(null);
  const [assemblyJobId, setAssemblyJobId] = useState('');
  const [assemblyJobStatus, setAssemblyJobStatus] = useState<any>(null);

  const handleComposeContent = async () => {
    if (!assemblyContent.trim()) {
      alert('Please enter content to compose');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'assembly' ? { ...e, status: 'processing', lastActivity: 'Composing content...', progress: 0 } : e
    ));
    
    try {
      const content = typeof assemblyContent === 'string' ? JSON.parse(assemblyContent) : assemblyContent;
      const assets = assemblyAssets ? (typeof assemblyAssets === 'string' ? JSON.parse(assemblyAssets) : assemblyAssets) : [];
      
      const response = await fetch(`${ASSEMBLY_ENGINE_URL}/api/assemble/compose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content,
          assets,
          template: assemblyTemplate || undefined
        })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setAssemblyResults(data.data);
        setAssemblyJobId(data.data.jobId);
        setEngines(prev => prev.map(e => 
          e.id === 'assembly' ? { ...e, status: 'completed', lastActivity: 'Composition started', progress: 100 } : e
        ));
      } else {
        throw new Error(data.error?.message || 'Composition failed');
      }
    } catch (error: any) {
      setAssemblyResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'assembly' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const handleCheckJobStatus = async () => {
    if (!assemblyJobId.trim()) {
      alert('Please enter a job ID');
      return;
    }
    
    try {
      const response = await fetch(`${ASSEMBLY_ENGINE_URL}/api/assemble/status/${assemblyJobId}`);
      const data = await response.json();
      if (data.status === 'success') {
        setAssemblyJobStatus(data.data);
      } else {
        throw new Error(data.error?.message || 'Failed to get job status');
      }
    } catch (error: any) {
      setAssemblyJobStatus({ error: error.message });
    }
  };

  const renderAssemblyEngine = () => (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
                    <button
            onClick={() => setViewMode('overview')}
            className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
                    </button>
          <h2 className="text-3xl font-bold text-[var(--text-pri)]">🔧 Assembly Engine</h2>
          <p className="text-[var(--text-sec)] mt-2">Compose and assemble content into deliverables</p>
        </div>
        <div className={`px-4 py-2  ${engines.find(e => e.id === 'assembly')?.health ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
          {engines.find(e => e.id === 'assembly')?.health ? 'Online' : 'Offline'}
                  </div>
                </div>

      {/* Compose Content Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5 text-[var(--text-ter)]" />
          Compose Content
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Combine content and assets into a complete deliverable.</p>
        
        <div className="space-y-4">
                  <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Content (JSON)</label>
            <textarea
              value={assemblyContent}
              onChange={(e) => setAssemblyContent(e.target.value)}
              placeholder='{"title": "...", "body": "..."}'
              rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2 font-mono text-sm"
            />
                            </div>
                            <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Assets (JSON array, optional)</label>
            <textarea
              value={assemblyAssets}
              onChange={(e) => setAssemblyAssets(e.target.value)}
              placeholder='[{"type": "image", "url": "..."}]'
              rows={3}
              className="w-full border border-[var(--rule)] px-3 py-2 font-mono text-sm"
            />
                            </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Template (optional)</label>
            <input
              type="text"
              value={assemblyTemplate}
              onChange={(e) => setAssemblyTemplate(e.target.value)}
              placeholder="template-name"
              className="w-full border border-[var(--rule)] px-3 py-2"
            />
                          </div>
                            <button
            onClick={handleComposeContent}
            disabled={!assemblyContent.trim()}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
                            >
            Compose Content
                            </button>

          {assemblyResults && (
            <div className="p-4 bg-[var(--surface)]">
              {assemblyResults.error ? (
                <p className="text-[var(--coral)]">Error: {assemblyResults.error}</p>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Composition Started:</h4>
                  <p className="text-sm text-[var(--text-sec)]">Job ID: {assemblyResults.jobId}</p>
                  <p className="text-sm text-[var(--text-sec)]">Status: {assemblyResults.status}</p>
                      <button
                    onClick={() => {
                      setAssemblyJobId(assemblyResults.jobId);
                      handleCheckJobStatus();
                    }}
                    className="mt-2 px-3 py-1 bg-[var(--slate)] text-white rounded text-xs hover:bg-[var(--slate)]"
                  >
                    Check Job Status
                      </button>
                  </div>
                )}
              </div>
            )}

          {assemblyJobStatus && (
            <div className="p-4 bg-[var(--slate-dim)]">
              <h4 className="font-semibold mb-2">Job Status:</h4>
              {assemblyJobStatus.error ? (
                <p className="text-[var(--coral)]">Error: {assemblyJobStatus.error}</p>
              ) : (
                <pre className="text-xs bg-[var(--bg-raised)] p-3 rounded border overflow-auto max-h-64">
                  {JSON.stringify(assemblyJobStatus, null, 2)}
                </pre>
              )}
                          </div>
          )}
                        </div>
                        </div>
                      </div>
  );

  // ========== PUBLISHING ENGINE FUNCTIONS ==========
  const [publishContent, setPublishContent] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [availablePlatforms, setAvailablePlatforms] = useState<any[]>([]);
  const [publishResults, setPublishResults] = useState<any>(null);
  const [batchPlatforms, setBatchPlatforms] = useState<string[]>([]);
  const [batchResults, setBatchResults] = useState<any>(null);

  const handleLoadPlatforms = async () => {
    try {
      const response = await fetch(`${PUBLISHING_ENGINE_URL}/api/platforms`);
      const data = await response.json();
      if (data.status === 'success') {
        setAvailablePlatforms(data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to load platforms:', error);
    }
  };

  useEffect(() => {
    if (viewMode === 'publishing') {
      handleLoadPlatforms();
    }
  }, [viewMode]);

  const handlePublishContent = async () => {
    if (!publishContent.trim() || !selectedPlatform) {
      alert('Please enter content and select a platform');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'publishing' ? { ...e, status: 'processing', lastActivity: 'Publishing content...', progress: 0 } : e
    ));
    
    try {
      const content = typeof publishContent === 'string' ? JSON.parse(publishContent) : publishContent;
      const response = await fetch(`${PUBLISHING_ENGINE_URL}/api/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, platform: selectedPlatform })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setPublishResults(data.data);
        setEngines(prev => prev.map(e => 
          e.id === 'publishing' ? { ...e, status: 'completed', lastActivity: 'Content published', progress: 100 } : e
        ));
      } else {
        throw new Error(data.error?.message || 'Publishing failed');
      }
    } catch (error: any) {
      setPublishResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'publishing' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const handleBatchTransform = async () => {
    if (!publishContent.trim() || batchPlatforms.length === 0) {
      alert('Please enter content and select at least one platform');
      return;
    }
    
    setEngines(prev => prev.map(e => 
      e.id === 'publishing' ? { ...e, status: 'processing', lastActivity: 'Transforming for multiple platforms...', progress: 0 } : e
    ));
    
    try {
      const content = typeof publishContent === 'string' ? JSON.parse(publishContent) : publishContent;
      const response = await fetch(`${PUBLISHING_ENGINE_URL}/api/batch-transform`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, platforms: batchPlatforms })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setBatchResults(data.data);
        setEngines(prev => prev.map(e => 
          e.id === 'publishing' ? { ...e, status: 'completed', lastActivity: `Transformed for ${batchPlatforms.length} platforms`, progress: 100 } : e
        ));
      } else {
        throw new Error(data.error?.message || 'Batch transformation failed');
      }
    } catch (error: any) {
      setBatchResults({ error: error.message });
      setEngines(prev => prev.map(e => 
        e.id === 'publishing' ? { ...e, status: 'error', lastActivity: `Error: ${error.message}` } : e
      ));
    }
  };

  const renderPublishingEngine = () => (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <button 
            onClick={() => setViewMode('overview')}
            className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
          </button>
          <h2 className="text-3xl font-bold text-[var(--text-pri)]">🚀 Publishing Engine</h2>
          <p className="text-[var(--text-sec)] mt-2">Transform and publish content across multiple platforms</p>
                          </div>
        <div className={`px-4 py-2  ${engines.find(e => e.id === 'publishing')?.health ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
          {engines.find(e => e.id === 'publishing')?.health ? 'Online' : 'Offline'}
                          </div>
                        </div>

      {/* Publish Content Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Rocket className="w-5 h-5 text-[var(--coral)]" />
          Publish Content
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Transform and publish content to a specific platform.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Content (JSON)</label>
            <textarea
              value={publishContent}
              onChange={(e) => setPublishContent(e.target.value)}
              placeholder='{"title": "...", "content": "...", "description": "..."}'
              rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2 font-mono text-sm"
            />
                    </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Platform</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="w-full border border-[var(--rule)] px-3 py-2"
            >
              <option value="">Select a platform...</option>
              {availablePlatforms.map((platform: any) => (
                <option key={platform.id} value={platform.id}>
                  {platform.name} (Max: {platform.maxLength} chars)
                </option>
              ))}
            </select>
                </div>
          <button
            onClick={handlePublishContent}
            disabled={!publishContent.trim() || !selectedPlatform}
            className="px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)] disabled:opacity-50"
          >
            Publish Content
          </button>

          {publishResults && (
            <div className="p-4 bg-[var(--surface)]">
              {publishResults.error ? (
                <p className="text-[var(--coral)]">Error: {publishResults.error}</p>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Publishing Result:</h4>
                  <pre className="text-xs bg-[var(--bg-raised)] p-3 rounded border overflow-auto max-h-96">
                    {JSON.stringify(publishResults, null, 2)}
                  </pre>
                          </div>
              )}
                          </div>
          )}
                        </div>
                      </div>

      {/* Batch Transform Section */}
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-[var(--text-ter)]" />
          Batch Transform
        </h3>
        <p className="text-[var(--text-sec)] mb-4">Transform content for multiple platforms at once.</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Content (JSON)</label>
            <textarea
              value={publishContent}
              onChange={(e) => setPublishContent(e.target.value)}
              placeholder='{"title": "...", "content": "...", "description": "..."}'
              rows={6}
              className="w-full border border-[var(--rule)] px-3 py-2 font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Select Platforms</label>
            <div className="space-y-2 max-h-48 overflow-y-auto border border-[var(--rule)] p-3">
              {availablePlatforms.map((platform: any) => (
                <label key={platform.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={batchPlatforms.includes(platform.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setBatchPlatforms([...batchPlatforms, platform.id]);
                      } else {
                        setBatchPlatforms(batchPlatforms.filter(p => p !== platform.id));
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{platform.name} (Max: {platform.maxLength} chars)</span>
                </label>
                    ))}
                  </div>
                </div>
                  <button
            onClick={handleBatchTransform}
            disabled={!publishContent.trim() || batchPlatforms.length === 0}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
                  >
            Transform for {batchPlatforms.length} Platform{batchPlatforms.length !== 1 ? 's' : ''}
                  </button>

          {batchResults && (
            <div className="p-4 bg-[var(--surface)]">
              {batchResults.error ? (
                <p className="text-[var(--coral)]">Error: {batchResults.error}</p>
              ) : (
                <div>
                  <h4 className="font-semibold mb-2">Batch Transform Results:</h4>
                  {batchResults.results && Object.keys(batchResults.results).length > 0 && (
                          <div className="space-y-3">
                      {Object.entries(batchResults.results).map(([platform, result]: [string, any]) => (
                        <div key={platform} className="p-3 bg-[var(--bg-raised)] rounded border">
                          <p className="font-medium text-sm text-[var(--text-pri)]">{platform}</p>
                          <p className="text-xs text-[var(--text-sec)] mt-1">
                            Length: {result.originalLength} chars, Within limits: {result.withinLimits ? '✅' : '⚠️'}
                          </p>
                          <details className="mt-2">
                            <summary className="text-xs text-[var(--text-sec)] cursor-pointer">View transformed content</summary>
                            <pre className="text-xs bg-[var(--surface)] p-2 rounded mt-1 overflow-auto max-h-32">
                              {result.transformed}
                            </pre>
                          </details>
                          </div>
                      ))}
                            </div>
                  )}
                  {batchResults.errors && Object.keys(batchResults.errors).length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-[var(--coral)]">Errors:</p>
                      {Object.entries(batchResults.errors).map(([platform, error]: [string, any]) => (
                        <p key={platform} className="text-xs text-[var(--coral)]">{platform}: {error}</p>
                      ))}
                            </div>
                  )}
                              </div>
                            )}
                          </div>
          )}
                            </div>
                              </div>
                              </div>
  );

  const renderOtherEngines = (engineId: string, engineName: string) => (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => setViewMode('overview')}
            className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
          </button>
          <h2 className="text-3xl font-bold text-[var(--text-pri)]">{engineName}</h2>
          <p className="text-[var(--text-sec)] mt-2">Tools and capabilities coming soon</p>
                            </div>
        <div className={`px-4 py-2  ${engines.find(e => e.id === engineId)?.health ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
          {engines.find(e => e.id === engineId)?.health ? 'Online' : 'Offline'}
                              </div>
                          </div>
      <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
        <p className="text-[var(--text-sec)]">This engine's tools are being developed. Check back soon!</p>
      </div>
                      </div>
                      );

  // ========== MAIN RENDER ==========
                      return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--text-pri)] mb-2">Run</h1>
          <p className="text-[var(--text-sec)]">Execute and monitor your content workflows. Access engine tools and manage operations.</p>
          <div className="mt-4 flex gap-4">
            <RouterLink 
              to="/home" 
              className="inline-flex items-center gap-2 text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
            >
              <ArrowRight className="h-4 w-4 rotate-180" />
              Back to Home
            </RouterLink>
            <RouterLink 
              to="/workflow" 
              className="inline-flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] font-medium"
            >
              <ArrowRight className="h-4 w-4" />
              View Workflow
            </RouterLink>
                  </div>
                  </div>

        {/* Main Content */}
        {viewMode === 'overview' && renderOverview()}
        {viewMode === 'prospector' && renderProspectorEngine()}
        {viewMode === 'curation' && renderCurationEngine()}
        {viewMode === 'authoring' && renderAuthoringEngine()}
        {viewMode === 'assembly' && renderAssemblyEngine()}
        {viewMode === 'publishing' && renderPublishingEngine()}
        {viewMode === 'knowledge' && renderOtherEngines('knowledge', '🧠 Knowledge Engine')}
        {viewMode === 'canon' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <button 
                  onClick={() => setViewMode('overview')}
                  className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Overview
                </button>
                <h2 className="text-3xl font-bold text-[var(--text-pri)]">📚 Canon Engine</h2>
                <p className="text-[var(--text-sec)] mt-2">Authoritative source of truth management</p>
              </div>
              <div className={`px-4 py-2  ${engines.find(e => e.id === 'canon')?.health ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
                {engines.find(e => e.id === 'canon')?.health ? 'Online' : 'Offline'}
          </div>
        </div>
        <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
              <p className="text-[var(--text-sec)] mb-4">Canon Engine has full-featured tools available.</p>
              <RouterLink
                to="/canon-engine"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
              >
                Open Canon Engine
                <ArrowRight className="w-4 h-4" />
              </RouterLink>
              </div>
          </div>
        )}
        {viewMode === 'orchestration' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <button 
                  onClick={() => setViewMode('overview')}
                  className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Overview
                </button>
                <h2 className="text-3xl font-bold text-[var(--text-pri)]">⚙️ Orchestration Engine</h2>
                <p className="text-[var(--text-sec)] mt-2">Job Execution State Model (JESM)</p>
              </div>
              <div className={`px-4 py-2  ${engines.find(e => e.id === 'orchestration')?.health ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
                {engines.find(e => e.id === 'orchestration')?.health ? 'Online' : 'Offline'}
          </div>
        </div>
        <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
              <p className="text-[var(--text-sec)] mb-4">Orchestration Engine provides full job lifecycle management with JESM.</p>
              <RouterLink
                to="/orchestration-engine"
                className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700"
              >
                Open Orchestration Engine
                <ArrowRight className="w-4 h-4" />
              </RouterLink>
              </div>
          </div>
        )}
        {viewMode === 'studio' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <button 
                  onClick={() => setViewMode('overview')}
                  className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Overview
                </button>
                <h2 className="text-3xl font-bold text-[var(--text-pri)]">🎨 Studio Engine</h2>
                <p className="text-[var(--text-sec)] mt-2">Professional asset creation and design tools</p>
              </div>
              <div className={`px-4 py-2  ${engines.find(e => e.id === 'studio')?.health ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
                {engines.find(e => e.id === 'studio')?.health ? 'Online' : 'Offline'}
          </div>
        </div>
        <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
              <p className="text-[var(--text-sec)] mb-4">Studio Engine has full-featured tools available.</p>
              <RouterLink
                to="/studio"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
              >
                Open Studio Engine
                <ArrowRight className="w-4 h-4" />
              </RouterLink>
              </div>
          </div>
        )}
      </div>
    </div>
  );
}
